import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import moment from 'moment';

import {ConfigService} from './config.service';
import {TransactionItem} from '../models/finance/transaction-item';
import {TransactionItemImport} from '../models/finance/transaction-item-import';
import {TransactionOverview} from '../models/finance/transaction-overview';
import {ImportTransactionItems} from '../models/finance/import-transaction-items';
import {TransactionItemFilterStatuses} from '../models/finance/transaction-item-filter-statuses';
import {Bank} from '../models/finance/bank';
import {TransactionRentMatchRule} from '../models/finance/transaction-rent-match-rule';
import {TenancySummary} from '../models/tenancy/tenancy-summary';
import {RentPaymentMatchPost} from '../models/rent-collection/rent-payment-match-post';
import {ManageOrganisationService} from './manage-organisation.service';
import {CreateMatchRulePostModel} from '../models/finance/transactions/create-match-rule-post-model';
import {GroupedTransactionAssociationAndStatus,} from '../models/finance/transactions/new-transactions-table/transaction-association';
import {SortOrder} from '../models/sort-order';
import {
    CreateTransactionFinancialItemRentSearch
} from '../models/finance/transactions/create-financial-item-from-transaction/create-transaction-financial-item-rent-search';
import {
    FinancialItemMatchProposal
} from '../models/finance/transactions/create-financial-item-from-transaction/financial-item-match-proposal';
import {DropdownOption} from '../components/dropdown/dropdown-option';
import {FormService} from './form.service';
import {BookkeepingTransaction} from '../models/finance/transactions/bookkeeping-transaction';
import {TransactionsTableSortColumn} from '../models/finance/transactions/transactions-table-sort-column';
import {BookkeepingTransactionsList} from '../models/finance/transactions/bookkeeping-transactions-list';
import {GroupedTransactionMatches} from '../models/finance/transactions/new-transactions-table/grouped-transaction-matches';
import { TransactionItemStatus } from '../models/finance/transaction-item-status';
import {StringMatchType} from '../models/finance/string-match-type';
import { TransactionsList } from '../models/finance/transactions/transactions-list';
import { PendingTransactionsList } from '../models/finance/transactions/pending-transactions-list';

@Injectable()
export class TransactionService {
    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private manageOrganisationService: ManageOrganisationService,
        private formService: FormService) {
    }

    matchRentPayment(organisationReference: string, propertyReference: string, roomReference: string, tenancyGuid: string, model: RentPaymentMatchPost): Observable<boolean> {
        let params = new HttpParams();
        if (tenancyGuid) {
            params = params.set('tenancyGuid', tenancyGuid);
        }

        return this.http.post<boolean>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/property/${ propertyReference }/room/${ roomReference }/transaction/rent-payments/match`, model, {params: params});
    }

    getOrganisationOverview(organisationReference: string) {
        return this.http.get<TransactionOverview>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/overview`);
    }

    getTransactionItems(
        organisationReference: string,
        page: number,
        pageSize: number,
        sortColumn: TransactionsTableSortColumn,
        sortOrder: SortOrder,
        startDate: string = null,
        endDate: string = null,
        description: string = null,
        amountFrom: number,
        amountTo: number,
        transactionFilterStatus: TransactionItemFilterStatuses,
        accountsFilterCsl: string,
        showSnoozed = false) {
        let params = new HttpParams()
            .set("page", page)
            .set("pageSize", pageSize)
            .set("sortOrder", sortOrder)
            .set("sortColumn", sortColumn);

        if (startDate != null) {
            params = params.set('startDate', moment(startDate).toISOString());
        }

        if (endDate != null) {
            params = params.set('endDate', moment(endDate).toISOString());
        }

        if (description != null) {
            params = params.set('description', description);
        }

        if (amountFrom != null) {
            params = params.set('amountFrom', amountFrom);
        }

        if (amountTo != null) {
            params = params.set('amountTo', amountTo);
        }

        if (accountsFilterCsl) params = params.set('accountsFilterCsl', accountsFilterCsl);

        if (transactionFilterStatus != null) {
            params = params.set('transactionFilterStatus', transactionFilterStatus);
        }

        params = params.set('includeOrganisationItems', true);
        params = params.set('showSnoozed', showSnoozed);

        return this.http.get<TransactionsList>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transaction-item`, { params });
    }

    getPendingTransactions(
        organisationReference: string,
        pageSize: number,
        page: number,
        sortColumn: TransactionsTableSortColumn,
        sortOrder: SortOrder) {
        const params = new HttpParams()
            .set("pageSize", pageSize)
            .set("page", page)
            .set("sortColumn", sortColumn)
            .set("sortOrder", sortOrder);

        return this.http.get<PendingTransactionsList>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/pending`, {params});
    }

    getTransactionItem(organisationReference: string, guid: string) {
        return this.http.get<TransactionItem>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transaction-item/${guid}`);
    }

    deleteTransactionItem(organisationReference: string, guid: string): Observable<number> {

        return this.http.delete<number>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transaction-item/${guid}`);
    }

    importTransactionItems(organisationReference: string, model: TransactionItemImport) : Observable<ImportTransactionItems> {
        return this.http.post<ImportTransactionItems>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/import-transaction-items`, model);
    }

    getBanksAndAccounts(organisationReference: string) : Observable<Bank[]> {
        return this.http.get<any>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/banks-and-accounts`);
    }

    buildAccountsDropdown(banksAndAccounts: Bank[]) {
        const banksAndAccountsFiltered = banksAndAccounts.filter(m => m.bankAccounts != null && m.bankAccounts.length > 0);
        let bankOptions: DropdownOption[] = [];

        banksAndAccountsFiltered.map(m => m.bankAccounts.map(a => bankOptions.push(new DropdownOption(a.name + (a.deleted ? ' - INACTIVE' : ''), a.reference, m.name))))

        bankOptions = this.formService.sortDropdownOptions(bankOptions);
        return bankOptions;
    }

    setTransactionIgnored(organisationReference: string, transactionGuid: string, ignored: boolean) {
        if(!organisationReference){
            organisationReference = this.manageOrganisationService.getOrganisationReference();
        }
        return this.http.post<{ isExcluded: boolean }>(`${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transaction-item/${transactionGuid}/set-ignored`, ignored);
    }

    setTransactionsNotRent(organisationReference: string, transactionGuid: string[]) : Observable<void> {
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/toggle-is-rent`;
        return this.http.post<void>(url, transactionGuid);
    }

    refreshAutoMatches(organisationReference: string) : Observable<void> {
        return this.http.post<void>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/refresh-auto-matches`, null);
    }

    retryAutoMatch(organisationReference: string, transactionGuids: string[]) {
        return this.http.post<void>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/retry-auto-matching`, transactionGuids);
    }

    syncTransactions(organisationReference: string, waitForSuccess: boolean = false) {
        let params = new HttpParams();
        params = params.set('waitForSuccess', waitForSuccess ? 'true' : 'false');

        return this.http.post<{saves: number}>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/sync?${ params.toString() }`, null);
    }

    syncTransactionsOneAccount(organisationReference: string, accountGuid: string) {
        return this.http.post<{saves: number}>(`${ this.configService.baseUrl }/manage/organisation/${ organisationReference }/transaction/sync/${accountGuid}`, null);
    }

    getTenancyOptions(organisationReference: string, propertyReference: string): Observable<TenancySummary[]> {
        let url: string;
        if(!propertyReference){
            url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/tenancy-options`
        } else {
            url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/property/${propertyReference}/transaction/tenancy-options`
        }
        return this.http.get<TenancySummary[]>(url);
    }

    getBookkeepingTransactions(
        dateFrom: string = null,
        dateTo: string = null,
        amountFrom: number,
        amountTo: number,
        filterStatus: TransactionItemFilterStatuses,
        descriptionMatchType: StringMatchType,
        description: string,
        payeeName: string,
        accounts: string[],
        showSnoozed: boolean,
        pageSize: number,
        page: number,
        sortColumn: TransactionsTableSortColumn,
        sortOrder: SortOrder){
        let params = new HttpParams()
            .set('pageSize', pageSize)
            .set('page', page)
            .set('sortColumn', sortColumn)
            .set('sortOrder', sortOrder);

        if(dateFrom){
            params = params.set('dateFrom', dateFrom)
        }
        if(dateTo){
            params = params.set('dateTo', dateTo)
        }
        if(amountFrom){
            params = params.set('amountFrom', amountFrom)
        }
        if(amountTo){
            params = params.set('amountTo', amountTo)
        }
        if(filterStatus){
            params = params.set('filterStatus', filterStatus)
        }
        if(description){
            params = params.set('descriptionMatchType', descriptionMatchType)
            params = params.set('description', description)
        }
        if(payeeName){
            params = params.set('payeeName', payeeName)
        }
        if(accounts?.length){
            accounts.forEach(m => params = params.append('accounts', m))
        }

        params = params.set('showSnoozed', showSnoozed)

        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/bookkeeping/transactions`;
        return this.http.get<BookkeepingTransactionsList>(url, {params});
    }

    getBookkeepingTransaction(transactionGuid: string){
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/bookkeeping/transactions/${transactionGuid}`;
        return this.http.get<BookkeepingTransaction>(url);
    }

    getTransactionAssociations(transactionGuid: string){
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/bookkeeping/transactions/${transactionGuid}/associations`;
        return this.http.get<GroupedTransactionAssociationAndStatus>(url);
    }

    setTransactionStatusRefresh(transactionGuid: string){
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/${transactionGuid}/set-status`;
        return this.http.patch<TransactionItemStatus>(url, null);
    }

    getTransactionMatches(transactionGuid: string){
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/bookkeeping/transactions/${transactionGuid}/matches`;
        return this.http.get<GroupedTransactionMatches[]>(url);
    }

    setTransactionSnoozed(transactionGuid: string, snoozeUntil: string) {
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        let params = new HttpParams()
        if(snoozeUntil){
            params = params.set('snoozeUntil', snoozeUntil);
        }
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transaction-item/${transactionGuid}/snooze`;
        return this.http.patch<{ snoozeUntil: string }>(url, null, {params});
    }

    searchFinancialItemMatch(transactionGuid: string, model: CreateTransactionFinancialItemRentSearch){
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        let params = new HttpParams()
            .set('expenseType', model.expenseType)
            .set('isReversal', model.isReversal)
            .set('categoryGuid', model.categoryGuid);

        if(model.propertyReference){
            params = params.set('propertyReference', model.propertyReference);
        }
        if(model.roomReference){
            params = params.set('roomReference', model.roomReference);
        }
        if(model.tenancyGuid){
            params = params.set('tenancyGuid', model.tenancyGuid);
        }
        if(model.supplierGuid){
            params = params.set('supplierGuid', model.supplierGuid);
        }
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/transactions-for-match/${transactionGuid}/search-financial-item-match`;
        return this.http.get<FinancialItemMatchProposal[]>(url, {params});
    }

    getRentPaymentTransaction(organisationReference: string, rentPaymentGuid: string){
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/from-rent-payment/${rentPaymentGuid}`;
        return this.http.get<TransactionItem>(url);
    }

    attachRentPaymentToTransaction(organisationReference: string, rentPaymentGuid: string, transactionGuid: string){
        const url = `${this.configService.baseUrl}/manage/organisation/${organisationReference}/transaction/${transactionGuid}/attach-rent-payment`;
        return this.http.post<void>(url, {
            rentPaymentGuid
        });
    }
}
